import { useCallback, useMemo, type ReactElement } from 'react'
import { find } from 'lodash-es'
import { Alert, Box, useTheme } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { useResultsStore } from '@editorStores'
import { RESULTS2_DETAIL_ROW_HIGHLIGHT_BG } from '../constants'
import { useHiddenSupportPosition } from '../hooks/useHiddenSupportPosition'
import {
  isSelectedSupportHighlightMatch,
  rangeOfSupport,
  toggleSelectedSupportHighlight,
} from '../utils'
import { CharacteristicSupportReactionCell } from './CharacteristicSupportReactionCell'

export const CharacteristicSupportReactionsTable = ({
  elementId,
}: {
  elementId?: string
}): ReactElement => {
  const theme = useTheme()
  const all = useResultsStore(state => state.characteristicMemberSupportReactions)
  const designForces = useResultsStore(state => state.designForces)
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)
  const data = useMemo(() => {
    if (!elementId || !all) return null
    return all.find(r => r.element_guid === elementId) ?? null
  }, [all, elementId])
  const designSupportForces = useMemo(() => {
    if (!elementId || !designForces) return []
    return find(designForces, ['element_guid', elementId])?.design_support_forces ?? []
  }, [designForces, elementId])

  // Hide the second support of rips/columns, matching the supports grid.
  const isHiddenSupportPosition = useHiddenSupportPosition(elementId)

  const handleRowClick = useCallback(
    (relativePosition: string | number) => () => {
      if (!elementId) return
      toggleSelectedSupportHighlight(
        elementId,
        relativePosition,
        highlightedCheck,
        setHighlightedCheck,
      )
    },
    [elementId, highlightedCheck, setHighlightedCheck],
  )

  if (!data) {
    if (Array.isArray(all) && all.length === 0) {
      return (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            borderRadius: 0,
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            borderTop: `1px solid ${theme.palette.divider}`,
            py: 0.75,
          }}
        >
          Es sind keine charakteristischen Auflagerkräfte verfügbar.
        </Alert>
      )
    }

    return (
      <EmptyState sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
        Charakteristische Auflagerkräfte sind nicht verfügbar
      </EmptyState>
    )
  }

  const { support_names: supportNames, rows } = data

  const visibleSupports = supportNames
    .map((name, supportIdx) => ({ name, supportIdx }))
    .filter(
      ({ supportIdx }) =>
        !isHiddenSupportPosition(designSupportForces[supportIdx]?.relative_position),
    )

  const hasPositionedLoadCases = rows.some(row => row.positioned_fields)

  const gridTemplateColumns = `minmax(96px, 1.4fr) ${rows.map(() => '1fr').join(' ')}`

  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          fontSize: 12,
          color: 'text.secondary',
          pb: 0.5,
        }}
      >
        <Box>Auflager [kN]</Box>
        {rows.map((row, colIdx) => (
          <Box key={`${row.load_case_label}-${colIdx}`}>{row.load_case_label}</Box>
        ))}
      </Box>

      {visibleSupports.length === 0 ? (
        <EmptyState sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          Keine charakteristischen Auflagerkräfte vorhanden
        </EmptyState>
      ) : null}

      {visibleSupports.map(({ name, supportIdx }) => {
        const relativePosition = designSupportForces[supportIdx]?.relative_position
        const isRowHighlighted =
          elementId != null &&
          isSelectedSupportHighlightMatch(elementId, highlightedCheck, relativePosition)

        return (
          <Box
            key={name}
            onClick={
              relativePosition != null && relativePosition !== ''
                ? handleRowClick(relativePosition)
                : undefined
            }
            sx={{
              display: 'grid',
              gridTemplateColumns,
              fontSize: 12,
              py: 0.625,
              borderTop: `1px solid ${theme.palette.divider}`,

              alignItems: hasPositionedLoadCases ? 'start' : 'center',
              cursor: relativePosition != null && relativePosition !== '' ? 'pointer' : 'default',
              backgroundColor: isRowHighlighted ? RESULTS2_DETAIL_ROW_HIGHLIGHT_BG : 'transparent',
            }}
          >
            <Box sx={{ fontFamily: 'Roboto' }}>{name}</Box>
            {rows.map((row, loadCaseIdx) => (
              <CharacteristicSupportReactionCell
                key={`${row.load_case_label}-${loadCaseIdx}`}
                force={row.forces_newton[supportIdx]}
                range={rangeOfSupport(row, supportIdx)}
              />
            ))}
          </Box>
        )
      })}

      {hasPositionedLoadCases ? (
        <Box
          sx={{
            fontSize: 11,
            color: 'text.secondary',
            pt: 0.75,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
          data-cy="characteristic-support-reactions-range-note"
        >
          Veränderliche Lasten mit maßgebender Laststellung: Wert bei Volllast, darunter min. und
          max. über alle feldweisen Laststellungen (belastete Felder am Info-Symbol).
        </Box>
      ) : null}
    </Box>
  )
}
